import React, { useEffect, useRef, useState } from "react";

import { useForm } from "../../useForm";

import Input from "../../../controls/Input";
import TextArea from "../../../controls/TextArea";

import ImportMediaDropzone from "../../../importMedia/ImportMediaDropzone";

import { config } from "../../../../constants";
import { classNames } from "../../../../shared/utility";
import SelectMenu from "../../../controls/SelectMenu";
import { readDefaultLocationFromStorage } from "../../../../shared/defaultLocationStorage";
import {
  fetchLocationsForDropdown,
  fetchLocationZonesForDropdown,
  normalizeDropdownOption,
} from "../../../../shared/locationDropdowns";

const initialFValues = {
  plu: "",
  name_en: "",
  name_fr: "",
  description_en: "",
  description_fr: "",
  pro_group: "",
  ingredients_en: "",
  ingredients_fr: "",
  weight: "",
  shelf_life: "",
  price: "",
  barcode: "",
  storage_description_en: "",
  storage_description_fr: "",
  cooking_instructions_en: "",
  cooking_instructions_fr: "",
  allergens_en: "",
  allergens_fr: "",
  type: { value: 0, label: "" },
  location: null,
  zone: null,
};

export default function ProductDetailsForm(props) {
  const {
    allergensOptions,
    product,
    actionCall,
    isEdit,
    unitOfMeasureOptions,
    productTypeOptions,
    onPrintDotBarcode,
  } = props;

  const [printingDotLabel, setPrintingDotLabel] = useState(false);
  const [uploadedImage1, setUploadedImage1] = useState(null);
  const [uploadedImage2, setUploadedImage2] = useState(null);
  const [uploadedNutritionImage, setUploadedNutritionImage] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const defaultLocationAppliedRef = useRef(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if ("type" in fieldValues) {
      temp.type = null;
      const typeValue = fieldValues.type?.value;
      if (typeValue == null || Number(typeValue) <= 0) {
        temp.type = "This field is required.";
      }
    }

    if ("zone" in fieldValues || "location" in fieldValues) {
      temp.zone = null;
      const zone = fieldValues.zone ?? values.zone;
      const location = fieldValues.location ?? values.location;
      if (zone?.value && !location?.value) {
        temp.zone = "Select a location before choosing a zone.";
      }
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInputChange,
    handleMultipleInputChange,
  } = useForm(initialFValues, true, validate);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      if (isEdit) {
        actionCall(product, values);
      } else {
        actionCall(values);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function loadLocations() {
      const callResult = await fetchLocationsForDropdown();
      if (!cancelled && callResult.success) {
        setLocationOptions(callResult.data ?? []);
      }
    }

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!values.location?.value) {
      setZoneOptions([]);
      return;
    }

    let cancelled = false;

    async function loadZones() {
      const callResult = await fetchLocationZonesForDropdown(values.location);
      if (!cancelled && callResult.success) {
        setZoneOptions(callResult.data ?? []);
      }
    }

    loadZones();

    return () => {
      cancelled = true;
    };
  }, [values.location?.value]);

  useEffect(() => {
    if (isEdit && product) {
      setValues({
        ...product,
        location: normalizeDropdownOption(product.location),
        zone: normalizeDropdownOption(product.zone),
      });
    }
  }, [isEdit, product, setValues]);

  useEffect(() => {
    if (isEdit || defaultLocationAppliedRef.current || values.location?.value) {
      return;
    }

    const defaultLocation = readDefaultLocationFromStorage();
    if (defaultLocation) {
      defaultLocationAppliedRef.current = true;
      setValues((prev) => ({ ...prev, location: defaultLocation }));
    }
  }, [isEdit, values.location?.value, setValues]);

  const handleLocationChange = (e) => {
    setValues((prev) => ({
      ...prev,
      location: e.target.value,
      zone: null,
    }));
  };

  const handlePrintDotBarcode = async () => {
    if (!onPrintDotBarcode) return;
    setPrintingDotLabel(true);
    try {
      await onPrintDotBarcode(values.dot_barcode);
    } finally {
      setPrintingDotLabel(false);
    }
  };

  const handleAllergensInput = (e) => {
    const newValues = e.target.value;
    var newAllergensEN = "";
    var newAllergensFR = "";
    newValues.forEach((element, index) => {
      if (index === 0) {
        newAllergensEN = element.label;
        newAllergensFR = element.label;
      } else {
        newAllergensEN += ", " + element.label;
        newAllergensFR += ", " + element.label;
      }
    });
    handleMultipleInputChange(
      { allergens: newValues },
      { allergens_en: newAllergensEN },
      { allergens_fr: newAllergensFR },
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="justify-between px-4">
        <div className={classNames(isEdit ? null : "divide-y divide-gray-500")}>
          <div
            className={classNames(
              isEdit ? "hidden" : null,
              "pt-6 pb-5 flex items-center justify-between flex-wrap sm:flex-nowrap",
            )}
          >
            <div className="mt-2">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Add new product
              </h3>
            </div>
          </div>

          {/* here */}
          <div className="space-y-3 pt-6 pb-5">
            <div>
              <div className="mt-3 grid grid-cols-4 gap-4 max-w-4xl">
                <Input
                  label="Plu"
                  name="plu"
                  onChange={handleInputChange}
                  value={values.plu}
                  labelOn={true}
                />
                <Input
                  label="Product Barcode"
                  name="product_barcode"
                  onChange={handleInputChange}
                  value={values.product_barcode}
                  labelOn={true}
                />
                <Input
                  label="Case Barcode"
                  name="barcode"
                  onChange={handleInputChange}
                  value={values.barcode}
                  labelOn={true}
                />
                <div className="flex flex-col gap-2">
                  <Input
                    label="DOT Barcode"
                    name="dot_barcode"
                    onChange={handleInputChange}
                    value={values.dot_barcode}
                    labelOn={true}
                  />
                  {onPrintDotBarcode ? (
                    <button
                      type="button"
                      disabled={printingDotLabel}
                      onClick={handlePrintDotBarcode}
                      className="inline-flex justify-center py-2 px-3 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {printingDotLabel ? "Printing…" : "Print DOT label"}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <Input
                  label="Name (EN)"
                  name="name_en"
                  onChange={handleInputChange}
                  value={values.name_en}
                  labelOn={true}
                />
                <Input
                  label="Name (FR)"
                  name="name_fr"
                  onChange={handleInputChange}
                  value={values.name_fr}
                  labelOn={true}
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <Input
                  label="Description (EN)"
                  name="description_en"
                  onChange={handleInputChange}
                  value={values.description_en}
                  labelOn={true}
                />
                <Input
                  label="Description (FR)"
                  name="description_fr"
                  onChange={handleInputChange}
                  value={values.description_fr}
                  labelOn={true}
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Ingredients (EN)"
                  labelOn={true}
                  name="ingredients_en"
                  onChange={handleInputChange}
                  value={values.ingredients_en}
                  rows="3"
                />
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Ingredients (FR)"
                  labelOn={true}
                  name="ingredients_fr"
                  onChange={handleInputChange}
                  value={values.ingredients_fr}
                  rows="3"
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="type"
                  options={productTypeOptions}
                  defaultValue={isEdit ? product.type : initialFValues.type}
                  title={"Product Type"}
                  error={errors.type}
                  showLabel={true}
                  value={values.type}
                />
              </div>
              <div className="mt-3 max-w-4xl">
                <p className="text-sm font-medium text-gray-900">
                  Default packaging location
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Used as the default storage location when packaging this
                  product.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <SelectMenu
                    handleInputChange={handleLocationChange}
                    name="location"
                    options={locationOptions}
                    value={values.location}
                    title="Location"
                    placeholder="Select location…"
                  />
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="zone"
                    options={zoneOptions}
                    value={values.zone}
                    title="Zone"
                    error={errors.zone}
                    placeholder={
                      values.location?.value
                        ? "Select zone…"
                        : "Select location first…"
                    }
                    disabled={!values.location?.value}
                  />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-4 max-w-4xl">
                <div>
                  <Input
                    label="Case weight"
                    name="weight"
                    onChange={handleInputChange}
                    value={values.weight}
                    labelOn={true}
                  />
                </div>
                <div>
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="unit_of_measure"
                    options={unitOfMeasureOptions}
                    defaultValue={isEdit ? product.unit_of_measure : null}
                    value={values.unit_of_measure}
                    title={"Case Unit of measure"}
                  />
                </div>
                <div>
                  <Input
                    label="Units in case"
                    name="unit_by_case"
                    onChange={handleInputChange}
                    value={values.unit_by_case}
                    labelOn={true}
                  />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-4 max-w-4xl">
                <div>
                  <Input
                    label="Individual Unit Size"
                    name="individual_unit_size"
                    onChange={handleInputChange}
                    value={values.individual_unit_size}
                    labelOn={true}
                  />
                </div>
                <div>
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="selling_unit_of_measure"
                    options={unitOfMeasureOptions}
                    defaultValue={
                      isEdit ? product.selling_unit_of_measure : null
                    }
                    value={values.selling_unit_of_measure}
                    title={"Selling Unit of measure"}
                  />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <div>
                  <Input
                    label="Unit Price"
                    name="price"
                    onChange={handleInputChange}
                    value={values.price}
                    labelOn={true}
                  />
                </div>
                <div>
                  <Input
                    label="Shelf life"
                    name="shelf_life"
                    onChange={handleInputChange}
                    value={values.shelf_life}
                    labelOn={true}
                  />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <div>
                  <Input
                    label="Storage description (EN)"
                    name="storage_description_en"
                    onChange={handleInputChange}
                    value={values.storage_description_en}
                    labelOn={true}
                  />
                </div>
                <div>
                  <Input
                    label="Storage description (FR)"
                    name="storage_description_fr"
                    onChange={handleInputChange}
                    value={values.storage_description_fr}
                    labelOn={true}
                  />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Cooking instructions (EN)"
                  labelOn={true}
                  name="cooking_instructions_en"
                  onChange={handleInputChange}
                  value={values.cooking_instructions_en}
                  rows="2"
                />
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Cooking instructions (FR)"
                  labelOn={true}
                  name="cooking_instructions_fr"
                  onChange={handleInputChange}
                  value={values.cooking_instructions_fr}
                  rows="2"
                />
              </div>
              <div className="mt-3 grid grid-cols-4 gap-4">
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-3 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Special Instructions 1"
                  labelOn={true}
                  name="special_instructions_1"
                  onChange={handleInputChange}
                  value={values.special_instructions_1}
                  rows="3"
                />
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-3 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Special Instructions 2"
                  labelOn={true}
                  name="special_instructions_2"
                  onChange={handleInputChange}
                  value={values.special_instructions_2}
                  rows="3"
                />
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-3 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Special Instructions 3"
                  labelOn={true}
                  name="special_instructions_3"
                  onChange={handleInputChange}
                  value={values.special_instructions_3}
                  rows="3"
                />
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-3 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
                  label="Special Instructions 4"
                  labelOn={true}
                  name="special_instructions_4"
                  onChange={handleInputChange}
                  value={values.special_instructions_4}
                  rows="3"
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 max-w-4xl">
                <div>
                  <SelectMenu
                    isMulti={true}
                    handleInputChange={(e) => {
                      handleAllergensInput(e);
                    }}
                    name="allergens"
                    options={allergensOptions}
                    defaultValue={isEdit ? product.allergens : null}
                    title={"Allergens"}
                    value={values.allergens}
                  />
                </div>
                <div>
                  <Input
                    label="Allergens (FR)"
                    name="allergens_fr"
                    disabled={true}
                    value={values.allergens_fr}
                    labelOn={true}
                  />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-4">
                <div className="grid-col-1">
                  <div className="flex space-x-2">
                    <label
                      htmlFor="image1"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Image 1
                    </label>
                  </div>
                  {uploadedImage1 ? (
                    <div>
                      <img
                        src={uploadedImage1}
                        height="200"
                        width="200"
                        alt="New logo"
                      />
                    </div>
                  ) : isEdit ? (
                    <img
                      src={config.url.STORAGE_URL + product.image1_url}
                      alt={product.name_en}
                      title={product.name_en}
                      className="media-image rounded-md"
                      height="200"
                      width="200"
                    />
                  ) : null}
                  <ImportMediaDropzone
                    fileName="image1"
                    handleInputChange={handleInputChange}
                    setUploadedImage={setUploadedImage1}
                  />
                </div>
                <div className="grid-col-1">
                  <div className="flex space-x-2">
                    <label
                      htmlFor="image2"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Image 2
                    </label>
                  </div>
                  {uploadedImage2 ? (
                    <div>
                      <img
                        src={uploadedImage2}
                        height="200"
                        width="200"
                        alt="New logo"
                      />
                    </div>
                  ) : isEdit ? (
                    product.image2_url ? (
                      <img
                        src={config.url.STORAGE_URL + product.image2_url}
                        alt={product.name_en}
                        title={product.name_en}
                        className="media-image rounded-md"
                        height="200"
                        width="200"
                      />
                    ) : null
                  ) : null}
                  <ImportMediaDropzone
                    fileName="image2"
                    handleInputChange={handleInputChange}
                    setUploadedImage={setUploadedImage2}
                  />
                </div>
                <div className="grid-col-1">
                  <div className="flex space-x-2">
                    <label
                      htmlFor="nutrition_image"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Nutrition Image
                    </label>
                  </div>
                  {uploadedNutritionImage ? (
                    <div>
                      <img
                        src={uploadedNutritionImage}
                        height="200"
                        width="200"
                        alt="New logo"
                      />
                    </div>
                  ) : isEdit ? (
                    product.image2_url ? (
                      <img
                        src={
                          config.url.STORAGE_URL + product.nutrition_image_url
                        }
                        alt={product.name_en}
                        title={product.name_en}
                        className="media-image rounded-md"
                        height="200"
                        width="200"
                      />
                    ) : null
                  ) : null}
                  <ImportMediaDropzone
                    fileName="nutrition_image"
                    handleInputChange={handleInputChange}
                    setUploadedImage={setUploadedNutritionImage}
                  />
                </div>
              </div>
            </div>

            <div className="sm:flex-no-wrap">
              <div className="my-auto flex items-center">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
